export type AssistantUsage = {
  inputTokens?: number;
  outputTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
};

export type AssistantAgentStats = {
  accumulatedInputTokens: number;
  accumulatedOutputTokens: number;
  accumulatedCachedInputTokens: number;
  accumulatedCachedOutputTokens: number;
  accumulatedTotalCost: number;
};

export type AssistantContextUsageEstimate = {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  usedContextTokens: number;
  generatedTokens: number;
  totalTokens: number;
};

type AssistantUsageCarrier = {
  role?: string;
  runId?: number;
  usage?: AssistantUsage;
};

function readFiniteTokenCount(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return Math.max(0, Math.round(value));
}

/**
 * Parses cumulative token counts from a gateway `token_progress` payload.
 * Returns undefined when no recognised numeric usage fields are present.
 */
export function parseUsageFromTokenPayload(tokens: unknown): AssistantUsage | undefined {
  if (!tokens) {
    return undefined;
  }
  if (typeof tokens === 'number' && Number.isFinite(tokens)) {
    return { inputTokens: Math.max(0, Math.round(tokens)) };
  }
  if (typeof tokens !== 'object' || Array.isArray(tokens)) {
    return undefined;
  }

  const record = tokens as Record<string, unknown>;
  const inputTokens = readFiniteTokenCount(record['inputTokens']);
  const outputTokens = readFiniteTokenCount(record['outputTokens']);
  const cacheReadTokens = readFiniteTokenCount(
    record['cacheReadTokens'] ?? record['cachedInputTokens'],
  );
  const cacheWriteTokens = readFiniteTokenCount(
    record['cacheWriteTokens'] ?? record['cachedOutputTokens'],
  );

  if (
    inputTokens === undefined
    && outputTokens === undefined
    && cacheReadTokens === undefined
    && cacheWriteTokens === undefined
  ) {
    return undefined;
  }

  return {
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheWriteTokens,
  };
}

export function hasUsageFields(usage: AssistantUsage): boolean {
  return usage.inputTokens !== undefined
    || usage.outputTokens !== undefined
    || usage.cacheReadTokens !== undefined
    || usage.cacheWriteTokens !== undefined;
}

export function usageDelta(nextValue: number | undefined, previousValue: number | undefined): number {
  return Math.max(0, (nextValue ?? 0) - (previousValue ?? 0));
}

/** Applies cumulative usage deltas to agent stats (matches assistantStore.applyUsage). */
export function applyUsageDeltaToAgentStats(
  stats: AssistantAgentStats,
  previousUsage: AssistantUsage | undefined,
  usage: AssistantUsage,
): void {
  stats.accumulatedInputTokens += usageDelta(usage.inputTokens, previousUsage?.inputTokens);
  stats.accumulatedOutputTokens += usageDelta(usage.outputTokens, previousUsage?.outputTokens);
  stats.accumulatedCachedInputTokens += usageDelta(usage.cacheReadTokens, previousUsage?.cacheReadTokens);
  stats.accumulatedCachedOutputTokens += usageDelta(usage.cacheWriteTokens, previousUsage?.cacheWriteTokens);
}

export function getPromptContextTokensForUsage(usage: AssistantUsage | undefined): number {
  if (usage?.inputTokens !== undefined) {
    return usage.inputTokens;
  }
  return (usage?.cacheReadTokens ?? 0) + (usage?.cacheWriteTokens ?? 0);
}

export function createAssistantContextUsageEstimate(
  usage: AssistantUsage | undefined,
): AssistantContextUsageEstimate {
  const inputTokens = usage?.inputTokens ?? 0;
  const outputTokens = usage?.outputTokens ?? 0;
  const cacheReadTokens = usage?.cacheReadTokens ?? 0;
  const cacheWriteTokens = usage?.cacheWriteTokens ?? 0;
  const usedContextTokens = getPromptContextTokensForUsage(usage);
  return {
    inputTokens,
    outputTokens,
    cacheReadTokens,
    cacheWriteTokens,
    usedContextTokens,
    generatedTokens: outputTokens,
    totalTokens: usedContextTokens + outputTokens,
  };
}

export function findCurrentConversationUsage(
  messages: readonly AssistantUsageCarrier[],
  activeRunId: number | null | undefined,
): AssistantUsage | undefined {
  if (activeRunId !== null && activeRunId !== undefined) {
    const activeMessage = messages.find(message => (
      message.role === 'assistant'
      && message.runId === activeRunId
      && message.usage !== undefined
    ));
    if (activeMessage?.usage) {
      return activeMessage.usage;
    }
  }

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === 'assistant' && message.usage) {
      return message.usage;
    }
  }

  return undefined;
}

/** Human-readable token progress for per-message footers. */
export function formatTokenProgress(tokens: unknown): string | null {
  const usage = parseUsageFromTokenPayload(tokens);
  if (!usage) {
    return null;
  }

  const parts: string[] = [];
  if (usage.inputTokens !== undefined) {
    parts.push(`in ${usage.inputTokens}`);
  }
  if (usage.outputTokens !== undefined) {
    parts.push(`out ${usage.outputTokens}`);
  }
  if (typeof tokens === 'object' && tokens !== null && !Array.isArray(tokens)) {
    const total = readFiniteTokenCount((tokens as Record<string, unknown>)['totalTokens']);
    if (total !== undefined) {
      parts.push(`total ${total}`);
    }
  }
  if (parts.length === 0) {
    return null;
  }
  return `Tokens ${parts.join(' • ')}`;
}
